import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellOff, X, Clock, Volume2, Mic, PlayCircle, Save, Upload, Trash2, Music, AlertTriangle } from 'lucide-react';
import { ReminderConfig, Mantra } from '../types';
import { getReminderConfig, saveReminderConfig, getMantras } from '../services/mockService';
import { cancelAllDivineNotifications, scheduleAllNotifications } from '../services/notificationChannels';
import {
  scheduleNativeDivineReminder,
  cancelNativeDivineReminder,
  startNativeService,
  stopNativeService,
} from '../services/nativeAlarmBridge';
import { NativeAlarmDebug } from './NativeAlarmDebug';
import { isNativeApp } from '../utils/platform';

let LocalNotifications: any = null;
if (isNativeApp()) {
  try {
    LocalNotifications = require('@capacitor/local-notifications').LocalNotifications;
  } catch {}
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

export const DivineReminder: React.FC<Props> = ({ isOpen, onClose, isAdmin = false }) => {
  const [config, setConfig] = useState<ReminderConfig>({
    enabled: false,
    intervalMinutes: 15,
    mantraText: 'श्री राम जय राम जय जय राम',
    volume: 1.0,
    useCustomAudio: false,
    customAudioData: undefined
  });
  
  const [mantras, setMantras] = useState<Mantra[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const adminAudioRef = useRef<HTMLInputElement>(null);
  const [mantraAudioMap, setMantraAudioMap] = useState<Record<string, string>>({});
  const [uploadingMantra, setUploadingMantra] = useState<string | null>(null);
  const [showAdminSection, setShowAdminSection] = useState(false);

  // Load config on mount
  useEffect(() => {
    const loaded = getReminderConfig();
    const loadedMantras = getMantras();
    // Backfill mantraId if missing (for existing saved configs)
    if (loaded.mantraText && !loaded.mantraId) {
      const m = loadedMantras.find(mt => mt.heading === loaded.mantraText || mt.text === loaded.mantraText);
      loaded.mantraId = m?.id || loaded.mantraText.replace(/\s+/g, '_').substring(0, 30);
      saveReminderConfig(loaded);
    }
    setConfig(loaded);
    setMantras(loadedMantras);
    // Fetch admin-uploaded mantra audio list
    fetchMantraAudioList();
  }, []);

  const fetchMantraAudioList = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_URL}/api/mantra-audio`);
      if (res.ok) {
        const list = await res.json();
        const map: Record<string, string> = {};
        list.forEach((item: any) => {
          map[item.mantra_id] = item.audio_url;
        });
        setMantraAudioMap(map);
        // Cache for DivineAlarmOverlay
        localStorage.setItem('mantra_audio_map', JSON.stringify(map));
      }
    } catch {}
  };

  const handleAdminAudioUpload = async (mantraId: string, mantraLabel: string, file: File) => {
    setUploadingMantra(mantraId);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('session_token') || '';
      const formData = new FormData();
      formData.append('mantra_id', mantraId);
      formData.append('mantra_label', mantraLabel);
      formData.append('audio', file);
      const res = await fetch(`${API_URL}/api/mantra-audio/upload`, {
        method: 'POST',
        headers: { 'X-Session-Token': token },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setMantraAudioMap(prev => {
          const updated = { ...prev, [mantraId]: data.audio_url };
          localStorage.setItem('mantra_audio_map', JSON.stringify(updated));
          return updated;
        });
      }
    } catch {}
    setUploadingMantra(null);
  };

  const handleDeleteMantraAudio = async (mantraId: string) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('session_token') || '';
      await fetch(`${API_URL}/api/mantra-audio/${mantraId}`, {
        method: 'DELETE',
        headers: { 'X-Session-Token': token },
      });
      setMantraAudioMap(prev => {
        const updated = { ...prev };
        delete updated[mantraId];
        localStorage.setItem('mantra_audio_map', JSON.stringify(updated));
        return updated;
      });
    } catch {}
  };

  // Test button → play TTS/admin audio / custom audio / chime
  const handleTest = () => {
    // Try audio from map (could be TTS or admin-uploaded)
    const match = mantras.find(m => m.heading === config.mantraText || m.text === config.mantraText);
    const mantraId = match?.id || config.mantraText.replace(/\s+/g, '_').substring(0, 30);
    if (mantraAudioMap[mantraId]) {
      let audioUrl = mantraAudioMap[mantraId];
      if (audioUrl.startsWith('/api/')) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        audioUrl = baseUrl + audioUrl;
      }
      const audio = new Audio(audioUrl);
      audio.volume = config.volume;
      audio.play().catch(() => {});
    } else if (config.useCustomAudio && config.customAudioData) {
      const audio = new Audio(config.customAudioData);
      audio.volume = config.volume;
      audio.play().catch(() => {});
    } else {
      // No audio yet - inform user to save first
      alert('पहले Save करें — audio auto-generate होगा');
    }
    if ('vibrate' in navigator) navigator.vibrate([300, 100, 300]);
  };

  // Cancel ALL divine notifications (native + local + FCM + service)
  const cancelAll = async () => {
    localStorage.removeItem('divine_last_audio_play');
    localStorage.removeItem('divine_next_fire_time');
    cancelNativeDivineReminder();
    stopNativeService();
    await cancelAllDivineNotifications();
  };

  // Map mantra IDs to Android res/raw filenames (without extension)
  const getMantraSoundName = (cfg: ReminderConfig): string => {
    const id = cfg.mantraId || '';
    const soundMap: Record<string, string> = {
      'ram': 'mantra_ram',
      'radha': 'mantra_radha',
      'radhakrishna': 'mantra_radhakrishna',
      'sitaram': 'mantra_sitaram',
      'sambsadashiv': 'mantra_sambsadashiv',
      'mahamantra': 'mantra_mahamantra',
      'shriharivansh': 'mantra_shriharivansh',
      'radhavallabh': 'mantra_radhavallabh',
      'shriharidas': 'mantra_shriharidas',
      'kunjbihari': 'mantra_kunjbihari',
      'shriramjayram': 'mantra_shriramjayram',
    };
    const textMap: Record<string, string> = {
      'Radhe Radhe': 'mantra_radhe_radhe',
      'Jai Shri Krishna': 'mantra_jai_shri_krishna',
      'Sitaram': 'mantra_sitaram_extra',
    };
    return soundMap[id] || textMap[cfg.mantraText] || 'mantra_ram';
  };

  // Schedule notifications on single SOUND channel
  const scheduleReminder = async (cfg: ReminderConfig) => {
    if (!cfg.enabled) return;

    const soundName = getMantraSoundName(cfg);
    const intervalMs = cfg.intervalMinutes * 60 * 1000;
    const nextFire = Date.now() + intervalMs;

    // Save sound name and next fire time in config
    const updatedCfg = { ...cfg, mantraSoundName: soundName, nextFireTime: nextFire };
    saveReminderConfig(updatedCfg);
    localStorage.setItem('divine_next_fire_time', String(nextFire));

    if (!isNativeApp()) return;

    // Layer 1: ForegroundService (MOST reliable — Android cannot kill this)
    try {
      const svcResult = startNativeService(cfg.intervalMinutes, cfg.mantraText, soundName);
      console.log('[DR] ForegroundService result:', svcResult);
    } catch (err) {
      console.error('[DR] ForegroundService failed:', err);
    }

    // Layer 2: AlarmManager backup (for when service somehow stops)
    try {
      const result = scheduleNativeDivineReminder(cfg.intervalMinutes, cfg.mantraText, soundName);
      console.log('[DR] Native AlarmManager result:', result);
    } catch (err) {
      console.error('[DR] Native alarm failed:', err);
    }

    // Layer 2: Capacitor LocalNotifications (secondary backup, if available)
    if (LocalNotifications) {
      try {
        const perm = await LocalNotifications.requestPermissions();
        if (perm.display === 'granted') {
          await cancelAllDivineNotifications();
          await scheduleAllNotifications(soundName, cfg.mantraText, intervalMs, Date.now());
          console.log('[DR] LocalNotifications scheduled');
        }
      } catch (err) {
        console.error('[DR] LocalNotif failed:', err);
      }
    }
  };

  const [generatingTTS, setGeneratingTTS] = useState(false);

  // Auto-generate TTS for a mantra if no audio exists
  const ensureMantraAudio = async (mantraText: string): Promise<void> => {
    if (config.useCustomAudio) return; // User chose custom audio, skip TTS

    const match = mantras.find(m => m.heading === mantraText || m.text === mantraText);
    const mantraId = config.mantraId || match?.id || mantraText.replace(/\s+/g, '_').substring(0, 30);
    
    // Save mantraId in config for overlay to use
    const updatedConfig = { ...config, mantraId };
    saveReminderConfig(updatedConfig);
    
    // Already have audio cached
    if (mantraAudioMap[mantraId]) return;

    setGeneratingTTS(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_URL}/api/mantra-audio/generate-tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mantra_id: mantraId, mantra_text: mantraText }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.audio_url) {
          setMantraAudioMap(prev => {
            const updated = { ...prev, [mantraId]: data.audio_url };
            localStorage.setItem('mantra_audio_map', JSON.stringify(updated));
            return updated;
          });
        }
      }
    } catch (err) {
      console.error('[DR] TTS generation failed:', err);
    }
    setGeneratingTTS(false);
  };

  // Sync divine reminder config to backend for FCM push delivery
  const syncToBackend = async (cfg: ReminderConfig, soundName: string) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('session_token') || '';
      const fcmToken = localStorage.getItem('fcm_token') || '';
      if (!token || !fcmToken) return;

      await fetch(`${API_URL}/api/divine-reminder/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': token,
        },
        body: JSON.stringify({
          enabled: cfg.enabled,
          interval_minutes: cfg.intervalMinutes,
          mantra_id: cfg.mantraId || soundName,
          mantra_text: cfg.mantraText,
          mantra_sound_name: soundName,
          fcm_token: fcmToken,
        }),
      });
      console.log('[DR] Synced to backend for FCM push');
    } catch (err) {
      console.error('[DR] Backend sync failed:', err);
    }
  };

  // Save settings
  const handleSave = async () => {
    saveReminderConfig(config);
    if (config.enabled) {
      // Auto-generate TTS audio for selected mantra
      await ensureMantraAudio(config.mantraText);
      // Schedule unified reminder (foreground JS timer + background native notifications)
      await scheduleReminder(config);
      // Sync to backend for FCM push delivery (works when app is killed)
      const soundName = getMantraSoundName(config);
      await syncToBackend(config, soundName);
    } else {
      // Disable everything
      saveReminderConfig({ ...config, enabled: false, nextFireTime: 0 });
      await cancelAll();
      // Disable on backend too
      await syncToBackend({ ...config, enabled: false }, 'mantra_ram');
    }
    onClose();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) { // 2MB Limit
        alert("Audio file is too large. Please use a file smaller than 2MB.");
        return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
        const result = event.target?.result as string;
        setConfig(prev => ({ ...prev, customAudioData: result, useCustomAudio: true }));
    };
    reader.readAsDataURL(file);
  };

  const clearAudio = () => {
      setConfig(prev => ({ ...prev, customAudioData: undefined, useCustomAudio: false }));
      if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
      <div className="bg-white dark:bg-stone-800 w-full max-w-md rounded-3xl shadow-2xl border-2 border-saffron dark:border-stone-700 overflow-hidden max-h-[90vh] overflow-y-auto custom-scrollbar">
        
        {/* Header */}
        <div className="bg-templeRed dark:bg-stone-900 p-5 flex justify-between items-center text-white sticky top-0 z-10">
           <div className="flex items-center gap-3">
              <Bell className="animate-pulse-slow" />
              <div>
                <h3 className="font-serif font-bold text-lg">Divine Reminder</h3>
                <p className="text-[10px] opacity-80 uppercase tracking-widest">Wake up the soul</p>
              </div>
           </div>
           <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-full transition-colors"><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
           
           {/* Toggle */}
           <div className="flex items-center justify-between bg-orange-50 dark:bg-stone-900 p-4 rounded-2xl border border-orange-100 dark:border-stone-700">
              <div className="flex items-center gap-3">
                 {config.enabled ? <Bell className="text-saffron" size={24} /> : <BellOff className="text-gray-400" size={24} />}
                 <div>
                    <h4 className="font-bold text-gray-800 dark:text-stone-200">Enable Alarm</h4>
                    <p className="text-xs text-gray-500 dark:text-stone-500">{config.enabled ? "Active" : "Paused"}</p>
                 </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={config.enabled} onChange={e => setConfig({...config, enabled: e.target.checked})} className="sr-only peer" />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-stone-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-saffron"></div>
              </label>
           </div>

           {/* Controls */}
           <div className={`space-y-6 transition-opacity ${!config.enabled ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
              
              {/* Interval */}
              <div className="space-y-2">
                 <label className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400 dark:text-stone-500">
                    <Clock size={14} /> Interval (Minutes)
                 </label>
                 <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 5, 10, 15, 30, 60, 120].map(mins => (
                       <button
                         key={mins}
                         onClick={() => setConfig({...config, intervalMinutes: mins})}
                         className={`py-2 rounded-xl text-xs font-bold transition-all ${config.intervalMinutes === mins ? 'bg-templeRed text-white shadow-md' : 'bg-gray-100 dark:bg-stone-900 text-gray-500 dark:text-stone-400 hover:bg-gray-200 dark:hover:bg-stone-700'}`}
                       >
                          {mins}m
                       </button>
                    ))}
                 </div>
              </div>

              {/* Reminder Type Selection */}
              <div className="space-y-3">
                  <label className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400 dark:text-stone-500">
                      <Mic size={14} /> Sound Source
                  </label>
                  <div className="flex bg-gray-100 dark:bg-stone-900 p-1 rounded-xl">
                      <button 
                        onClick={() => setConfig({ ...config, useCustomAudio: false })}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${!config.useCustomAudio ? 'bg-white dark:bg-stone-800 text-templeRed dark:text-saffron shadow-sm' : 'text-gray-400 dark:text-stone-500'}`}
                      >
                         <Mic size={14} /> Synthesized
                      </button>
                      <button 
                        onClick={() => setConfig({ ...config, useCustomAudio: true })}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-2 ${config.useCustomAudio ? 'bg-white dark:bg-stone-800 text-templeRed dark:text-saffron shadow-sm' : 'text-gray-400 dark:text-stone-500'}`}
                      >
                         <Music size={14} /> Custom Audio
                      </button>
                  </div>

                  {/* Dynamic Content based on Selection */}
                  {!config.useCustomAudio ? (
                    <div className="animate-fade-in">
                        <select 
                            value={config.mantraText}
                            onChange={e => {
                              const val = e.target.value;
                              const m = mantras.find(mt => mt.heading === val);
                              setConfig({...config, mantraText: val, mantraId: m?.id || val.replace(/\s+/g, '_').substring(0, 30)});
                            }}
                            className="w-full p-3 rounded-xl bg-gray-50 dark:bg-stone-900 border border-gray-200 dark:border-stone-700 font-hindi font-bold text-gray-800 dark:text-stone-200 outline-none focus:border-saffron"
                            >
                            {mantras.map(m => (
                                <option key={m.id} value={m.heading}>{m.heading}</option>
                            ))}
                            <option value="Radhe Radhe">Radhe Radhe</option>
                            <option value="Jai Shri Krishna">Jai Shri Krishna</option>
                            <option value="Sitaram">Sitaram</option>
                        </select>
                    </div>
                  ) : (
                    <div className="animate-fade-in space-y-3">
                        {config.customAudioData ? (
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-xl p-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center text-green-600 dark:text-green-400">
                                        <Music size={16} />
                                    </div>
                                    <div className="text-xs font-bold text-green-800 dark:text-green-300">
                                        Audio Loaded
                                    </div>
                                </div>
                                <button onClick={clearAudio} className="p-1.5 bg-white dark:bg-stone-800 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ) : (
                            <div 
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-gray-300 dark:border-stone-700 rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-saffron hover:bg-orange-50 dark:hover:bg-stone-900/50 transition-all text-gray-400 dark:text-stone-500"
                            >
                                <Upload size={24} />
                                <span className="text-xs font-bold uppercase tracking-wide">Click to Upload Audio</span>
                                <span className="text-[9px] opacity-70">MP3/WAV (Max 2MB)</span>
                                <input 
                                    type="file" 
                                    accept="audio/*" 
                                    ref={fileInputRef} 
                                    className="hidden" 
                                    onChange={handleFileUpload} 
                                />
                            </div>
                        )}
                    </div>
                  )}
              </div>

              {/* Volume */}
              <div className="space-y-2">
                 <label className="flex items-center justify-between text-xs font-black uppercase tracking-widest text-gray-400 dark:text-stone-500">
                    <div className="flex items-center gap-2"><Volume2 size={14} /> Voice Volume</div>
                    <span>{Math.round(config.volume * 100)}%</span>
                 </label>
                 <input 
                   type="range" min="0" max="1" step="0.1" 
                   value={config.volume}
                   onChange={e => setConfig({...config, volume: parseFloat(e.target.value)})}
                   className="w-full h-2 bg-gray-200 dark:bg-stone-700 rounded-full appearance-none accent-saffron"
                 />
              </div>

              <button 
                onClick={handleTest}
                className="w-full py-3 bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-stone-300 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors"
                data-testid="divine-test-sound"
              >
                 <PlayCircle size={18} /> Test Sound
              </button>

              {/* DEBUG: Native Alarm Test — ALWAYS visible for diagnosis */}
              <NativeAlarmDebug />

              {/* Xiaomi/MIUI Info Note */}
              {isNativeApp() && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 text-xs text-amber-700 dark:text-amber-300 space-y-1" data-testid="xiaomi-note">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-bold">Xiaomi/MIUI Users:</p>
                      <p>Settings &gt; Apps &gt; इस app &gt; Battery &gt; No restrictions चुनें + Autostart ON करें</p>
                      <p className="mt-1 font-semibold">Audio app खुली रहने पर ही बजेगा</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Audio Management */}
              {isAdmin && (
                <div className="space-y-3" data-testid="admin-audio-section">
                  <button
                    onClick={() => setShowAdminSection(!showAdminSection)}
                    className="w-full py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-xs font-bold text-red-700 dark:text-red-300 flex items-center justify-center gap-2"
                  >
                    <Music size={14} /> Admin: Mantra Audio Manage {showAdminSection ? '▲' : '▼'}
                  </button>
                  
                  {showAdminSection && (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      <input
                        type="file"
                        accept="audio/*"
                        ref={adminAudioRef}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          const mantraId = adminAudioRef.current?.dataset.mantraId || '';
                          const mantraLabel = adminAudioRef.current?.dataset.mantraLabel || '';
                          if (file && mantraId) {
                            handleAdminAudioUpload(mantraId, mantraLabel, file);
                          }
                          e.target.value = '';
                        }}
                      />
                      {mantras.map(m => (
                        <div key={m.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-stone-800 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold truncate">{m.text}</p>
                            {mantraAudioMap[m.id] ? (
                              <span className="text-[10px] text-green-600 dark:text-green-400 font-bold">Audio Uploaded</span>
                            ) : (
                              <span className="text-[10px] text-gray-400">No audio</span>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            {mantraAudioMap[m.id] && (
                              <>
                                <button
                                  onClick={() => { 
                                    let url = mantraAudioMap[m.id];
                                    if (url.startsWith('/api/')) url = (process.env.REACT_APP_BACKEND_URL || '') + url;
                                    const a = new Audio(url); a.play().catch(() => {}); 
                                  }}
                                  className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded text-green-700 dark:text-green-300"
                                  title="Play"
                                >
                                  <PlayCircle size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteMantraAudio(m.id)}
                                  className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded text-red-700 dark:text-red-300"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                if (adminAudioRef.current) {
                                  adminAudioRef.current.dataset.mantraId = m.id;
                                  adminAudioRef.current.dataset.mantraLabel = m.text;
                                  adminAudioRef.current.click();
                                }
                              }}
                              disabled={uploadingMantra === m.id}
                              className="p-1.5 bg-saffron/20 rounded text-saffron disabled:opacity-50"
                              title="Upload"
                            >
                              {uploadingMantra === m.id ? (
                                <span className="text-[10px]">...</span>
                              ) : (
                                <Upload size={14} />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

           </div>

           <button 
             onClick={handleSave}
             disabled={generatingTTS}
             className="w-full py-4 bg-saffron text-stone-900 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg hover:bg-orange-600 transition-all active:scale-95 disabled:opacity-70"
             data-testid="divine-save-btn"
           >
              {generatingTTS ? (
                <>
                  <span className="animate-spin inline-block w-5 h-5 border-2 border-stone-900/30 border-t-stone-900 rounded-full" />
                  Mantra Audio बन रहा है...
                </>
              ) : (
                <>
                  <Save size={20} /> Save Settings
                </>
              )}
           </button>

        </div>
      </div>
    </div>
  );
};